import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

/**
 * Catches render errors in routed page content so one broken page shows an in-page message
 * instead of blanking the whole app. The surrounding layout (header, footer, admin shell) keeps rendering.
 * Pass the pathname as `resetKey` (or key it by pathname) so navigating to another page clears the error.
 * `resetKey` resets without remounting the children, which keeps shared shells like the dashboard mounted.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.reset();
  }

  componentDidCatch(error, info) {
    // There is no client error-reporting endpoint yet; keep a clear console trail for debugging.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Page crashed:', error, info && info.componentStack);
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { homeTo = '/', homeLabel = 'Go to home' } = this.props;
    return (
      <div className="grid min-h-[50vh] place-items-center px-4 py-12 text-center" role="alert">
        <div className="max-w-md">
          <span className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-danger-soft text-danger">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="font-display text-xl font-extrabold text-ink">Something went wrong loading this page</h1>
          <p className="mt-2 text-sm text-muted">Try again, or head back and pick up where you left off. If it keeps happening, let us know.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button type="button" className="btn-primary btn-sm" onClick={this.reset}>
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
            </button>
            <Link to={homeTo} className="btn-outline btn-sm" onClick={this.reset}>
              <Home className="h-4 w-4" aria-hidden="true" /> {homeLabel}
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
